import { useEffect, useState } from "react";
import { AnimatePresence, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { CourtyardProvider, useCourtyard } from "@/context/CourtyardContext";
import Opening from "@/components/invite/Opening";
import Hero from "@/components/invite/Hero";
import SectionNav from "@/components/invite/SectionNav";
import Marquee from "@/components/invite/Marquee";
import MessageBand from "@/components/invite/MessageBand";
import Rules from "@/components/invite/Rules";
import Activities from "@/components/invite/Activities";
import RsvpBand from "@/components/invite/RsvpBand";
import RsvpModal from "@/components/invite/RsvpModal";
import PhotoWall from "@/components/invite/PhotoWall";
import Footer from "@/components/invite/Footer";
import StickyRsvp from "@/components/invite/StickyRsvp";

function useLenis(enabled) {
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!enabled || reduce) return undefined;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    window.__lenis = lenis;
    let id = requestAnimationFrame(function raf(t) {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    });
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      window.__lenis = null;
    };
  }, [enabled, reduce]);
}

function Inner() {
  const { event, eventError } = useCourtyard();
  const [intro, setIntro] = useState(() => sessionStorage.getItem("ddd_entered") !== "1");
  useLenis(!intro);

  useEffect(() => {
    document.body.style.overflow = intro ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [intro]);

  const enter = () => {
    sessionStorage.setItem("ddd_entered", "1");
    setIntro(false);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <AnimatePresence>{intro && <Opening key="opening" ev={event} onEnter={enter} />}</AnimatePresence>
      <main className="paper min-h-screen" aria-hidden={intro || undefined}>
        {eventError && (
          <div className="mx-auto max-w-xl px-6 py-32 text-center" data-testid="event-load-error">
            <h1 className="font-poster text-4xl text-[var(--navy)]">The diyas are still warming up.</h1>
            <p className="mt-4">We couldn't load the invitation details. Please refresh in a moment.</p>
          </div>
        )}
        {event && (
          <>
            <Hero ready={!intro} />
            <SectionNav />
            <Marquee />
            <MessageBand />
            <Rules />
            <Activities />
            <RsvpBand />
            <PhotoWall />
            <Footer onReplay={() => setIntro(true)} />
            <RsvpModal />
            <StickyRsvp />
          </>
        )}
      </main>
    </>
  );
}

export default function Invitation() {
  return (
    <CourtyardProvider>
      <Inner />
    </CourtyardProvider>
  );
}
